import React, {useEffect, useState} from "react";
import { BrowserRouter, Routes, Route, Navigate} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Messenger from "./components/Messenger";
import { AppDispatch, RootState } from "./redux/store";

import { checkWhatsAppNumber, loginSession } from "./redux/reducers/authSlice";
import { fetchDealDetails, fetchPersonDetails } from "./redux/reducers/dealPersonDetailsSlice";
import GlobalNotification from "./components/GlobalNotification";
import AuthorizationRequiredScreen from "./components/auth/AuthorizationRequiredScreen";
import AuthStatusScreen from "./components/auth/AuthStatusScreen";
import WhatsAppConnectionRequiredScreen from "./components/auth/WhatsAppConnectionRequiredScreen";
import SubscriptionWarningModal, {
  SUBSCRIPTION_WARNING_MODAL_DISMISSIBLE,
} from "./components/auth/SubscriptionWarningModal";

const AuthLoadingSpinner = () => (
  <div className="mt-6 flex justify-center">
    <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-200 border-t-[#25D366]" />
  </div>
);

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const {
    isAuthenticated,
    isAuthenticating,
    isCheckingWhatsApp,
    authorizationRequired,
    whatsappConnectionRequired,
    whatsappChecked,
    isSubscribed,
    subscriptionMessage,
    token,
    error,
  } = useSelector((state: RootState) => state.auth);
  const [subscriptionWarningDismissed, setSubscriptionWarningDismissed] =
    useState(false);

  useEffect(() => {
    setSubscriptionWarningDismissed(false);
  }, [isSubscribed, subscriptionMessage]);

  const showSubscriptionWarning =
    isAuthenticated &&
    !isSubscribed &&
    (!SUBSCRIPTION_WARNING_MODAL_DISMISSIBLE || !subscriptionWarningDismissed);

  if (isAuthenticating) {
    return (
      <AuthStatusScreen
        title="Verifying your session"
        description="Please wait while we connect your Pipedrive workspace."
      >
        <AuthLoadingSpinner />
      </AuthStatusScreen>
    );
  }

  if (!isAuthenticated && authorizationRequired) {
    return <AuthorizationRequiredScreen />;
  }

  if (isCheckingWhatsApp || (token && !whatsappChecked && !whatsappConnectionRequired)) {
    return (
      <AuthStatusScreen
        title="Checking WhatsApp"
        description="Please wait while we verify your WhatsApp number connection."
      >
        <AuthLoadingSpinner />
      </AuthStatusScreen>
    );
  }

  if (!isAuthenticated && whatsappConnectionRequired && token) {
    return <WhatsAppConnectionRequiredScreen />;
  }

  // Show error message if authentication failed (network / server error)
  if (!isAuthenticated && error) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-default-background">
        <div className="flex flex-col items-center gap-6 max-w-md mx-auto text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Failed</h2>
            <p className="text-gray-600 mb-4">
              {typeof error === "string"
                ? error
                : "We couldn't verify your credentials. Please check your userId and companyId parameters and try again."}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated && !token) {
    return (
      <AuthStatusScreen
        title="Welcome"
        description="Please wait while we verify your credentials..."
      >
        <AuthLoadingSpinner />
      </AuthStatusScreen>
    );
  }

  
  // WhatsApp connected + authenticated — subscription false shows blocking modal (dismiss optional via flag)
  return (
    <>
      {children}
      {showSubscriptionWarning && (
        <SubscriptionWarningModal
          onClose={() => setSubscriptionWarningDismissed(true)}
        />
      )}
    </>
  );
};

function AppContent() {
  const dispatch = useDispatch<AppDispatch>();
  const { token, whatsappChecked, isCheckingWhatsApp } = useSelector(
    (state: RootState) => state.auth
  );
  // const navigate = useNavigate();
  // const { isAuthenticated} = useSelector<RootState, { 
  //   isAuthenticated: boolean;
  //   isAuthenticating: boolean;
  //   error: string | boolean | undefined;
  // }>(
  //   (state) => state.auth
  // );  
   // eslint-disable-next-line @typescript-eslint/no-explicit-any
  //  const [sdk, setSdk] = useState<any>(null);
   // eslint-disable-next-line @typescript-eslint/no-explicit-any
  //  const [Command, setCommand] = useState<any>(null);

  useEffect(() => {
    const initSDK = async () => {
      try {
        const { default: AppExtensionsSDK, Command } = await import(
          '@pipedrive/app-extensions-sdk'
        );
        const sdkInstance = new AppExtensionsSDK();

        await sdkInstance.initialize();
        // setSdk(sdkInstance);
        // setCommand(Command);

        // Initial resize with width and minimum height
        await sdkInstance.execute(Command.RESIZE, { 
          width: 1100, 
          height: 750 
        });


        // URL parameters are now handled in separate useEffects

        // setUserId(userId || '24740492');
        // setCompanyId(companyId || '');
        // setToken(token || '');
        // setId(id || '');
        // setResource(resource || '');
        // setView(view || '');
        // setSelectedIds(selectedIds || '');

        // Fetch templates from API if userId is available
        // if (userId) {
        //   try {
        //     const response = await fetch(
        //       `https://api.insightssystem.com/api:MLBAaPmt/pipedrive/user/templates?pdUserId=${userId}`
        //     );
            
        //     if (response.ok) {
        //       const data = await response.json();
        //       setApiData(data);
        //     } else {
        //      console.log(response)
        //     }
        //   } catch (fetchError) {
        //     console.log(fetchError);
        //   }
        // }

        // Fetch sender info
        // if (userId) {
        //   try {
        //     const senderResponse = await fetch(
        //       `https://api.insightssystem.com/api:MLBAaPmt/pipedrive/whatsapp/user/info?pdUserId=${userId}`
        //     );
            
        //     if (senderResponse.ok) {
        //       const senderData = await senderResponse.json();
        //       const verifiedName = senderData?.verified_name || 'Unknown Sender';
        //       const phoneNumber = senderData?.display_phone_number || '';
        //       const formattedSenderInfo = phoneNumber 
        //         ? `${verifiedName} (${phoneNumber})` 
        //         : verifiedName;
        //       setSenderInfo(formattedSenderInfo);
        //     } else {
             
        //       setSenderInfo('Unknown Sender');
        //     }
        //   } catch (senderError) {
        
        //     setSenderInfo('Unknown Sender');
        //   }
        // }

        // Fetch labels
        // if (userId) {
        //   try {
        //     const labelsResponse = await fetch(
        //       `https://api.insightssystem.com/api:MLBAaPmt/pipedrive/label?pdUserId=${userId}&team_id=0`
        //     );
            
        //     if (labelsResponse.ok) {
        //       const labelsData = await labelsResponse.json();
        //       setLabels(labelsData);
        //     } else {
          
        //       setLabels([]);
        //     }
        //   } catch (labelsError) {
         
        //     setLabels([]);
        //   }
        // }

        // Note: Deal details are now fetched in a separate useEffect using Redux

        // Note: Person details are now fetched in a separate useEffect using Redux
        

      } catch (error) {
        // Failed to initialize SDK
        console.log(error)
      }
    };

    initSDK();
  }, [dispatch]);

  // Check for userId and companyId in URL parameters and dispatch loginSession if they exist
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const userId = urlParams.get('userId');
    const companyId = urlParams.get('companyId');

    if (userId && companyId) {

      // Don't call setIsAuthenticating here - it's handled by loginSession action
      dispatch(loginSession({ userId, companyId }));
    }
  }, [dispatch]);

  // After session success, verify WhatsApp number is connected
  useEffect(() => {
    if (token && !whatsappChecked && !isCheckingWhatsApp) {
      const urlParams = new URLSearchParams(window.location.search);
      const userId = urlParams.get("userId");
      const companyId = urlParams.get("companyId");

      if (userId && companyId) {
        dispatch(checkWhatsAppNumber({ userId, companyId }));
      }
    }
  }, [token, whatsappChecked, isCheckingWhatsApp, dispatch]);

  // Fetch deal or person details when component loads and conditions are met
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const userId = urlParams.get('userId');
    const companyId = urlParams.get('companyId');
    const resource = urlParams.get('resource');
    const view = urlParams.get('view');
    const selectedIds = urlParams.get('selectedIds');

    if (userId && companyId && view === 'details' && selectedIds) {
      if (resource === 'deal') {
        dispatch(fetchDealDetails({ userId, companyId, dealId: selectedIds }));
      } else if (resource === 'person') {
        dispatch(fetchPersonDetails({ userId, companyId, personId: selectedIds }));
      }
    }
  }, [dispatch]);

  // Clean up URL parameters after successful login
  // useEffect(() => {
  //   if (isAuthenticated && window.location.search) {
      
  //     navigate("/", { replace: true });
  //   }
  // }, [isAuthenticated, navigate]);

  return (
    <>
      <GlobalNotification />
      <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Messenger />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
